import { useState, useEffect, useCallback } from "react";
import { carregarModalidadesDoNucleo, carregarDiasTreinoFisico, salvarDiasTreinoFisico } from "../firebase";
import { AZUL, VERDE_CLARO, CINZA_ESC, TEXTO, BRANCO } from "../tema";
import { Card, Btn, StatusBox } from "../componentes";

// Dias da semana úteis (1=segunda ... 5=sexta), como no restante do sistema.
const DIAS = [
  { n: 1, curto: "Seg", nome: "Segunda" },
  { n: 2, curto: "Ter", nome: "Terça" },
  { n: 3, curto: "Qua", nome: "Quarta" },
  { n: 4, curto: "Qui", nome: "Quinta" },
  { n: 5, curto: "Sex", nome: "Sexta" },
];

// Aba do PREPARADOR: define, por modalidade, quais dias da semana têm treino
// FÍSICO. É um padrão semanal recorrente que perdura até ser alterado. Nos
// dias marcados, a presença física fica aberta para registro; nos demais,
// fechada (não há treino físico). Isolado por núcleo. NÃO afeta o cálculo
// de frequência oficial — é um módulo informativo à parte.
export default function SecaoTreinoFisicoConfig({ userNucleo }) {
  const [modalidades, setModalidades] = useState([]);
  const [config, setConfig] = useState({}); // { modalidadeLower: [1,3,5] }
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(null); // modalidade sendo salva
  const [status, setStatus] = useState(null);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setStatus(null);
    try {
      const [mods, cfg] = await Promise.all([
        carregarModalidadesDoNucleo(userNucleo),
        carregarDiasTreinoFisico(userNucleo),
      ]);
      setModalidades(mods || []);
      setConfig(cfg || {});
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao carregar: " + (e.message || "") });
    } finally {
      setCarregando(false);
    }
  }, [userNucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  function alternarDia(modLower, diaN) {
    setConfig(c => {
      const atuais = c[modLower] || [];
      const novos = atuais.includes(diaN)
        ? atuais.filter(d => d !== diaN)
        : [...atuais, diaN].sort();
      return { ...c, [modLower]: novos };
    });
  }

  async function salvar(modalidade) {
    const modLower = modalidade.toLowerCase();
    setSalvando(modLower);
    setStatus(null);
    try {
      await salvarDiasTreinoFisico({
        nucleo: userNucleo,
        modalidade,
        diasSemana: config[modLower] || [],
      });
      setStatus({ tipo: "ok", msg: `Dias de treino físico de ${modalidade} salvos.` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao salvar: " + (e.message || "") });
    } finally {
      setSalvando(null);
    }
  }

  if (carregando) {
    return (
      <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "32px 24px" }}>
        <div style={{ fontSize: 14 }}>Carregando modalidades…</div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Dias de treino físico</div>
        <div style={{ fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>
          Marque os dias da semana em que cada modalidade tem treino físico. Esse padrão vale
          todas as semanas até você alterar. Nos dias marcados, a presença física fica aberta
          para registro; nos demais, fechada. Isto não altera a frequência oficial dos
          atletas, serve para acompanhar o cumprimento dos treinos físicos.
        </div>
      </Card>

      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      {modalidades.length === 0 ? (
        <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "28px 24px" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 14 }}>Nenhuma modalidade cadastrada neste núcleo.</div>
        </Card>
      ) : (
        modalidades.map(mod => {
          const modLower = mod.toLowerCase();
          const dias = config[modLower] || [];
          return (
            <Card key={mod} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: TEXTO }}>{mod}</div>
                <div style={{ fontSize: 12, color: CINZA_ESC }}>
                  {dias.length === 0 ? "Sem treino físico configurado" : `${dias.length} dia${dias.length > 1 ? "s" : ""} por semana`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {DIAS.map(d => {
                  const ativo = dias.includes(d.n);
                  return (
                    <button
                      key={d.n}
                      onClick={() => alternarDia(modLower, d.n)}
                      style={{
                        padding: "10px 16px", borderRadius: 10, fontSize: 14, fontWeight: 700,
                        cursor: "pointer", minWidth: 62,
                        border: ativo ? `2px solid ${AZUL}` : "2px solid #E5E7EB",
                        background: ativo ? VERDE_CLARO : BRANCO,
                        color: ativo ? AZUL : CINZA_ESC,
                        transition: "all .15s",
                      }}
                    >
                      {d.curto}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn onClick={() => salvar(mod)} disabled={salvando === modLower}>
                  {salvando === modLower ? "Salvando…" : "Salvar dias"}
                </Btn>
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}
